import React, { useEffect, useRef } from "react";

export default function VideoContentPlayer({ content, fileUrl, progress, onTrack, onContextChange, navigationRequest }) {
    const videoRef = useRef(null);
    const resumedRef = useRef(false);
    const handledNavigationRef = useRef(null);

    useEffect(() => {
        const videoElement = videoRef.current;
        if (!videoElement) return undefined;

        const savedSecond = Number(progress?.progressPayload?.currentSecond || 0);
        const handleLoadedMetadata = () => {
            if (!resumedRef.current && savedSecond > 0 && savedSecond < Math.max(0, videoElement.duration - 2)) {
                videoElement.currentTime = savedSecond;
                resumedRef.current = true;
            }
            onContextChange?.({
                contentTypeContext: "VIDEO",
                videoTimestampSeconds: Math.floor(Number(videoElement.currentTime || 0)),
            });

            onTrack?.({
                completionPercent: Math.round(Number(progress?.progressPayload?.watchedPercent || progress?.progressPercent || 0)),
                progressPayload: {
                    currentSecond: Number(videoElement.currentTime || 0).toFixed(1),
                    durationSeconds: Number(videoElement.duration || 0).toFixed(1),
                    watchedPercent: Math.round(videoElement.duration ? (videoElement.currentTime / videoElement.duration) * 100 : 0),
                    reachedEnd: false,
                },
            });
        };

        const emitSnapshot = (immediate = false, reachedEnd = false, deltaSeconds = 0) => {
            const duration = Number(videoElement.duration || 0);
            const currentSecond = Number(videoElement.currentTime || 0);
            const watchedPercent = Math.round(duration > 0 ? (currentSecond / duration) * 100 : 0);
            onContextChange?.({
                contentTypeContext: "VIDEO",
                videoTimestampSeconds: Math.floor(currentSecond),
            });

            onTrack?.({
                timeSpentDeltaSeconds: deltaSeconds,
                completionPercent: reachedEnd ? 100 : watchedPercent,
                progressPayload: {
                    currentSecond: Number(currentSecond.toFixed(1)),
                    durationSeconds: Number(duration.toFixed(1)),
                    watchedPercent,
                    reachedEnd,
                },
                immediate,
            });
        };

        const handlePause = () => emitSnapshot(true, false, 0);
        const handleEnded = () => emitSnapshot(true, true, 0);

        let intervalId = null;
        const handlePlay = () => {
            if (intervalId) return;
            intervalId = window.setInterval(() => {
                if (!document.hidden && !videoElement.paused && !videoElement.ended) {
                    emitSnapshot(false, false, 1);
                }
            }, 1000);
        };
        const stopInterval = () => {
            if (intervalId) {
                window.clearInterval(intervalId);
                intervalId = null;
            }
        };

        videoElement.addEventListener("loadedmetadata", handleLoadedMetadata);
        videoElement.addEventListener("play", handlePlay);
        videoElement.addEventListener("pause", handlePause);
        videoElement.addEventListener("ended", handleEnded);

        return () => {
            stopInterval();
            videoElement.removeEventListener("loadedmetadata", handleLoadedMetadata);
            videoElement.removeEventListener("play", handlePlay);
            videoElement.removeEventListener("pause", handlePause);
            videoElement.removeEventListener("ended", handleEnded);
        };
    }, [content?.id, fileUrl, onContextChange, onTrack]);

    useEffect(() => {
        const videoElement = videoRef.current;
        const requestId = navigationRequest?.requestId;
        const second = navigationRequest?.videoTimestampSeconds;
        if (!videoElement || !requestId || second == null || handledNavigationRef.current === requestId) return;
        handledNavigationRef.current = requestId;
        videoElement.currentTime = Math.max(0, Number(second || 0));
        videoElement.focus();
    }, [navigationRequest]);

    return (
        <video ref={videoRef} className="cr-video" controls src={fileUrl}>
            Votre navigateur ne prend pas en charge cette video.
        </video>
    );
}
